import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Copy } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Panel, inputCls } from "@/components/Peep";

const LABEL = { media_viewed: "Viewed media", profile_viewed: "Viewed profile", live_joined: "Joined live", live_ended: "Ended live", content_unpublished: "Unpublished content" };

function SetupLink({ r }) {
  if (!r) return null;
  return (
    <div data-testid="review-setup-result" className="mt-3 rounded-xl border border-[#d42be8]/40 bg-[#d42be8]/10 p-3 text-xs text-[#f0a8fa]">
      <p>{r.email_sent ? "A single-use setup link was emailed to the review address." : "Email couldn't be sent — copy the link below and open it yourself."} It works once and expires in {r.expires_in_hours} hours.</p>
      <button type="button" onClick={() => { navigator.clipboard?.writeText(r.setup_link); toast.success("Setup link copied"); }} data-testid="review-setup-copy" className="mt-2 flex items-center gap-1 rounded-lg border border-[#d42be8]/50 px-3 py-1.5"><Copy size={12} /> Copy setup link</button>
    </div>
  );
}

function CreateForm({ onDone }) {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const body = { username: String(f.get("username") || "").trim(), email: String(f.get("email") || "").trim() };
    setBusy(true);
    try {
      const { data } = await api.post("/admin/owner-review", body);
      toast.success("Owner Review account created");
      setRes(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  if (res) return <div><p className="text-sm">@{res.account.username} · {res.account.email}</p><SetupLink r={res} /><button onClick={onDone} className="mt-3 text-xs underline">Done</button></div>;
  return (
    <form onSubmit={submit} className="grid gap-2 md:max-w-md" noValidate>
      <p className="text-xs text-[#a7a0af]">One-time setup. Use an email that isn't already a PEEPING account (for example you+review@gmail.com). You'll get a single-use link to choose the password yourself — it's never shown to anyone or stored in plain text.</p>
      <input name="username" autoComplete="off" autoCapitalize="none" data-testid="review-username" placeholder="Username (letters, numbers, _ .)" className={inputCls} />
      <input name="email" type="email" autoComplete="off" data-testid="review-email" placeholder="Review account email" className={inputCls} />
      <button disabled={busy} data-testid="review-create-btn" className="btn-brand mt-1 rounded-xl py-3 text-sm font-semibold">{busy ? "Creating…" : "Create account & send setup link"}</button>
    </form>
  );
}

function PendingSetup() {
  const [res, setRes] = useState(null);
  const send = async () => {
    try { setRes((await api.post("/admin/owner-review/setup-link")).data); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="mt-2">
      <p className="text-xs text-amber-200">Password not set yet.</p>
      <button onClick={send} data-testid="review-setup-resend" className="mt-2 rounded-lg border border-white/15 px-3 py-1.5 text-xs">Send a new setup link</button>
      <SetupLink r={res} />
    </div>
  );
}

export default function AdminOwnerReview() {
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    api.get("/admin/owner-review").then((r) => setD(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <p className="text-sm text-[#a7a0af]">Loading…</p>;
  return (
    <div data-testid="admin-owner-review" className="flex flex-col gap-6">
      <div>
        <h1 className="text-3xl font-extrabold">Owner Review</h1>
        <p className="mt-1 text-sm text-[#a7a0af]">A separate PLATFORM_OWNER_REVIEW account that sees the real subscriber app with every creator's content unlocked — no subscriptions, no payments, no finance or banking access.</p>
      </div>
      <Panel title="Account" action={<ShieldCheck size={14} className="text-[#a7a0af]" />}>
        {d.exists ? (
          <div data-testid="review-account-info" className="text-sm">
            <p>@{d.account.username} · {d.account.email}</p>
            <p className="mt-1 text-xs text-[#a7a0af]">Use the "Owner Review" button in the top bar to switch. To change its password use Forgot Password on the login screen.</p>
            {d.account.password_set === false && <PendingSetup />}
          </div>
        ) : <CreateForm onDone={load} />}
      </Panel>
      <Panel title="Review & moderation log">
        {d.log.length === 0 && <p data-testid="review-log-empty" className="text-sm text-[#a7a0af]">No review activity yet.</p>}
        {d.log.map((r) => (
          <div key={r.id} data-testid="review-log-row" className="flex flex-wrap justify-between gap-2 border-t border-white/5 py-2 text-xs">
            <span><span className="font-semibold text-white">{LABEL[r.action] || r.action}</span> <span className="text-[#a7a0af]">· {r.creator_name || "—"} · {r.target}{r.reason ? ` · reason: ${r.reason}` : ""}</span></span>
            <span className="text-[#6f6878]">@{r.username} · {new Date(r.created_at).toLocaleString()}</span>
          </div>
        ))}
      </Panel>
    </div>
  );
}
